import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import {
  Plus, Copy, Trash2, Check, Key, AlertTriangle, Shield
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from '@/components/ui/dialog'
import {
  Table, TableHeader, TableRow, TableHead,
  TableBody, TableCell, TableWrapper
} from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import { listAPIKeys, createAPIKey, revokeAPIKey } from '@/lib/api'
import type { APIKey } from '@/lib/api'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatRelative(iso?: string): string {
  if (!iso) return 'Never'
  const now = Date.now()
  const diff = now - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function KeysPage() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [createOpen, setCreateOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState<APIKey | null>(null)
  const [newKeyName, setNewKeyName] = useState('')
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const { data: keys = [], isLoading } = useQuery<APIKey[]>({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      return listAPIKeys(token)
    },
  })

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      return createAPIKey(name, token)
    },
    onSuccess: (data) => {
      setCreatedKey(data.key)
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      toast({ title: 'API key created!', variant: 'success' })
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to create key', description: err.message, variant: 'error' })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: async (keyId: string) => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      return revokeAPIKey(keyId, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      toast({ title: 'API key revoked', variant: 'success' })
      setRevokeOpen(false)
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to revoke key', description: err.message, variant: 'error' })
    },
  })

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
    toast({ title: 'Copied to clipboard', variant: 'success', duration: 2000 })
  }

  const handleCreate = async () => {
    if (!newKeyName.trim()) return
    await createMutation.mutateAsync(newKeyName.trim())
  }

  const handleCloseCreate = () => {
    setCreateOpen(false)
    setNewKeyName('')
    setCreatedKey(null)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">API Keys</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your Neoserve API keys</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Create Key
        </Button>
      </div>

      {/* Security note */}
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <strong>Keep your API keys secure.</strong> Never share them publicly or commit them
            to source control. Keys have access to your account and incur charges.
          </div>
        </CardContent>
      </Card>

      {/* Keys table */}
      <TableWrapper>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Key Prefix</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Rate Limit</TableHead>
              <TableHead>Last Used</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                  Loading keys...
                </TableCell>
              </TableRow>
            ) : keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <Key className="h-8 w-8" />
                    <p className="font-medium">No API keys yet</p>
                    <p className="text-xs">Create your first API key to start using Neoserve</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              keys.map(key => (
                <TableRow key={key.id}>
                  <TableCell className="font-medium">{key.name}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                      {key.prefix}...
                    </code>
                  </TableCell>
                  <TableCell>
                    <Badge variant={key.status === 'active' ? 'success' : 'destructive'}>
                      {key.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-500">{key.rate_limit_rpm} rpm</TableCell>
                  <TableCell className="text-gray-500">{formatRelative(key.last_used_at)}</TableCell>
                  <TableCell className="text-gray-500">{formatDate(key.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {key.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setSelectedKey(key)
                          setRevokeOpen(true)
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {/* Create Key Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) handleCloseCreate(); else setCreateOpen(true) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Give your key a descriptive name so you can identify it later.
            </DialogDescription>
          </DialogHeader>

          {createdKey ? (
            <div className="space-y-4">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                <strong>Copy your key now!</strong> You won't be able to see it again after closing this dialog.
              </div>
              <div>
                <Label>Your new API key</Label>
                <div className="flex gap-2 mt-1">
                  <code className="flex-1 text-xs bg-gray-900 text-green-400 px-3 py-2.5 rounded-lg font-mono truncate">
                    {createdKey}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopy(createdKey, 'created')}
                  >
                    {copiedId === 'created' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCloseCreate}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="key-name" required>Key name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g. Production API Key"
                  value={newKeyName}
                  onChange={e => setNewKeyName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseCreate}>Cancel</Button>
                <Button
                  onClick={handleCreate}
                  loading={createMutation.isPending}
                  disabled={!newKeyName.trim()}
                >
                  Create Key
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke <strong>{selectedKey?.name}</strong>? This action cannot
              be undone. Any applications using this key will lose access immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              Key prefix: <code className="font-mono">{selectedKey?.prefix}...</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              loading={revokeMutation.isPending}
              onClick={() => selectedKey && revokeMutation.mutate(selectedKey.id)}
            >
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
